<template>
	<div class="preview">

		<!-- HEADER -->
		<div class="preview__header">
			<div class="preview__header-left">
				<p class="preview__label">Our Portfolio</p>
				<h2>Recent Projects<span></span></h2>
			</div>
			<router-link to="/our-portfolio" class="preview__link-desktop">
				View all projects →
			</router-link>
		</div>

		<!-- GRID -->
		<div class="preview__grid">
			<div
				v-for="(image, i) in previewImages"
				:key="i"
				class="preview__card"
				@click="$router.push('/our-portfolio')"
			>
				<div class="preview__card-img">
					<img :src="image.src[0]" :alt="image.alt || 'K&M Remodeling project'" loading="lazy">
					<div class="preview__card-overlay">
						<span>View All Projects →</span>
					</div>
					<div class="preview__card-badge">{{ image.serviceTitle }}</div>
				</div>
				<div class="preview__card-body">
					<p class="preview__card-title">{{ image.title }}</p>
					<span class="preview__card-year">{{ image.year }}</span>
				</div>
			</div>
		</div>

		<!-- CTA MOBILE -->
		<div class="preview__footer">
			<router-link to="/our-portfolio" class="preview__btn">
				View all {{ totalCount }} projects →
			</router-link>
		</div>

	</div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
	name: 'PortfolioPreview',

	computed: {
		...mapGetters(['getHomepagePreview', 'getServices']),

		previewImages() {
			return this.getHomepagePreview;
		},

		totalCount() {
			return this.getServices.flatMap(s => s.details.images || []).length;
		},
	},
};
</script>

<style lang="scss" scoped>
.preview {
	padding: 0 30px;
	margin: 50px 0;

	@media (max-width: 768px) { padding: 0 15px; margin: 30px 0; }
}

// ─── HEADER ──────────────────────────────────────────────
.preview__header {
	display: flex;
	justify-content: space-between;
	align-items: flex-end;
	margin-bottom: 28px;
}

.preview__label {
	font-size: 11px;
	letter-spacing: 0.09em;
	text-transform: uppercase;
	color: rgba(25,25,25,0.4);
	margin-bottom: 10px;
}

h2 {
	font-size: 42px;
	font-weight: 500;
	color: #191919;
	margin: 0;
	line-height: 1.1;

	span {
		font-family: 'Pinyon Script', cursive;
		font-size: 54px;
		font-weight: 400;
	}

	@media (max-width: 768px) {
		font-size: 28px;
		span { font-size: 36px; }
	}
}

.preview__link-desktop {
	font-size: 14px;
	font-weight: 600;
	color: #191919;
	text-decoration: none;
	white-space: nowrap;
	padding-bottom: 2px;
	border-bottom: 1px solid rgba(25,25,25,0.2);
	transition: border-color 0.2s ease;
	margin-bottom: 6px;

	&:hover { border-color: #191919; }

	@media (max-width: 500px) { display: none; }
}

// ─── GRID ────────────────────────────────────────────────
.preview__grid {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 16px;

	@media (max-width: 768px) { grid-template-columns: repeat(2, 1fr); }
	@media (max-width: 500px) { grid-template-columns: 1fr; gap: 12px; }
}

// ─── CARD ────────────────────────────────────────────────
.preview__card {
	cursor: pointer;
	border-radius: 12px;
	overflow: hidden;
	border: 1px solid rgba(0,0,0,0.07);
	background: #fff;
	transition: transform 0.25s ease, box-shadow 0.25s ease;

	&:hover {
		transform: translateY(-3px);
		box-shadow: 0 8px 28px rgba(0,0,0,0.09);

		.preview__card-overlay { opacity: 1; }
		.preview__card-img img { transform: scale(1.05); }
	}
}

.preview__card-img {
	position: relative;
	aspect-ratio: 4/3;
	overflow: hidden;
	background: #f0f0f0;

	img {
		width: 100%; height: 100%;
		object-fit: cover; display: block;
		transition: transform 0.4s ease;
	}
}

.preview__card-overlay {
	position: absolute; inset: 0;
	background: rgba(0,0,0,0.3);
	display: flex; align-items: center; justify-content: center;
	opacity: 0;
	transition: opacity 0.25s ease;

	span {
		color: #fff;
		font-size: 13px;
		font-weight: 600;
		letter-spacing: 0.03em;
		border: 1px solid rgba(255,255,255,0.7);
		padding: 7px 18px;
		border-radius: 100px;
	}
}

.preview__card-badge {
	position: absolute; top: 10px; left: 10px;
	background: rgba(65,77,67,0.88);
	color: #fff;
	font-size: 10px; font-weight: 700;
	letter-spacing: 0.05em; text-transform: uppercase;
	padding: 3px 9px; border-radius: 100px;
}

.preview__card-body {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 12px 14px;
	gap: 8px;
}

.preview__card-title {
	font-size: 13px; font-weight: 500;
	color: #191919; margin: 0; line-height: 1.3; flex: 1;
}

.preview__card-year {
	font-size: 11px; color: rgba(25,25,25,0.4);
	flex-shrink: 0; font-weight: 500;
}

// ─── FOOTER ──────────────────────────────────────────────
.preview__footer {
	display: flex;
	justify-content: center;
	margin-top: 28px;
}

.preview__btn {
	display: inline-flex;
	align-items: center;
	padding: 11px 28px;
	border-radius: 100px;
	border: 1px solid rgba(0,0,0,0.15);
	background: #ffffff;
	font-size: 14px;
	font-weight: 600;
	color: #191919;
	text-decoration: none;
	transition: all 0.2s ease;

	&:hover {
		background: #191919;
		color: #ffffff;
		border-color: #191919;
	}
}
</style>